import {Component, Mixins, Watch} from "vue-property-decorator";
import {
    createFormSubmission,
    downloadFormSubmission,
    getFormByName,
    getFormSubmission,
    updateFormSubmission
} from "@/services/FormServices";
import {
    Form,
    FormSection,
    FormSectionField,
    FormSectionViewModel,
    FormSubmission,
    FormSubmissionField,
    FormSubmissionUpload,
    FormSubmissionUploadViewModel,
    FormUpload,
    KeyField,
    SectionGridRow,
} from "@/FormModels";
import InputControl from "@/components/InputControl.vue";
import UploadControl from "@/components/UploadControl.vue";
import {Fragment} from 'vue-fragment';
import VueRecaptcha from 'vue-recaptcha';
import LoadingMixin from "@/mixins/LoadingMixin";
import {downloadFile} from "@/utils/downloadUtil";
import Modal from "@/components/Modal.vue";
import {getQueryParams} from "@/utils/queryParamUtil";
import OverflowTextControl from "@/components/OverflowTextControl.vue";
import {HTTP} from '@/services/HttpServices';


export interface OverflowedElement {
    keyIndex: number;
    labelName: string;
    fullText: string;
}

    
interface CaptchaResult {
    captchaResponse: string;
}

@Component({
    components: {
        InputControl,
        Fragment,
        UploadControl,
        Modal,
        OverflowTextControl,
        VueRecaptcha
    },
    filters: {
        removeFolder(fileName: string) {
            if (!fileName.includes('/')) {
                return fileName;
            }
            return fileName.substring(fileName.lastIndexOf('/') + 1);
        }
    }

})
export default class CustomForm extends Mixins(LoadingMixin) {
    private readonly SUBMIT_BUTTON_ID = 'custom-form-external-submit';
    private readonly PDF_BUTTON_ID = 'custom-form-external-pdf';

    private form = {} as Form;
    private formSubmission: FormSubmission = {
        id: '',
        formId: 0,
        submittedOn: undefined as undefined | Date,
        formSubmissionUploads: [] as FormSubmissionUpload[],
        formSubmissionFields: [] as FormSubmissionField[],
        userSavedFormOn: undefined as undefined | Date,
        url: '',
        saveForLater: false,
        printedToPdf: false
    };

    private fields = [] as FormSubmissionField[];
    private sections = [] as FormSectionViewModel[];
    private uploads = [] as FormSubmissionUploadViewModel[];
    private showModal = false;
    private primaryEmailField = {} as FormSubmissionField;
    private tempEmailField = '';
    private autoSaving = false;
    private overflowedElements = [] as OverflowedElement[];

    private captchaSiteKey = '';
    private captchaResponse = '';
    private captchaError = '';
    private isCaptchaSuccess = true;
    private isCaptchaReady = false;            

    storeResponse(response: any) {
        this.captchaResponse = response;
        this.isCaptchaSuccess = true;
    }

    handleExpiration() {
        this.isCaptchaSuccess = false;
    }

    handleRecaptchaError() {
        this.captchaError = 'Captcha failed. Please refresh this page and try again.';
        this.isCaptchaSuccess = false;
    }  

    async registerCaptcha() {
        await this.execute(async () => {
            const { captchaSiteKey } = await HTTP.get(`/public/captcha`);
            this.captchaSiteKey = captchaSiteKey;
            this.isCaptchaReady = true;
        }, 'Failed to retrieve captcha data');
    }


    get formSubmitted(): boolean {
        return this.formSubmission && Boolean(this.formSubmission.submittedOn);
    }
    get showOverFlowText(): boolean {
        const querystring = location.search.replace('?','');
        if (querystring) {
            const queries = querystring.split('&');
            if (queries && queries.find(query => query.startsWith('showOverflowText=y'))) {
                return true;
            }
        }
        return false;
    }

    private readonly LEAVE_WARNING = 'Data has changed on the form.  Are you sure you want to leave? Please submit or create a PDF or save and finish later to preserver your data.';

    @Watch('fields', {deep: true})
    private fieldsChanged() {
        window.onbeforeunload = () => this.LEAVE_WARNING;
    }


    @Watch('uploads', {deep: true})
    private async uploadsChanged() {
        window.onbeforeunload = () => this.LEAVE_WARNING;
        if (this.primaryEmailField && this.primaryEmailField.fieldValue && !this.autoSaving) {
            this.autoSave();
        }
    }

    private async dataChanged() {
        if (this.primaryEmailField && this.primaryEmailField.fieldValue && !this.autoSaving) {
            this.autoSave();
        }
    }

    private async created() {
        await this.loadData();
        window.onbeforeunload = null;

        this.addListenerToExternalButtons();

        const main = document.getElementsByClassName("main");
        if (main[0]) {
            const finishLaterButton = document.getElementById("finish-later-button");
            if (finishLaterButton && finishLaterButton.parentElement) {
                finishLaterButton.parentElement.removeChild(finishLaterButton);
                main[0].appendChild(finishLaterButton);
            }
        }
        
        this.registerCaptcha();
    }

    private async getOverflowData() {
        this.$nextTick(function () {
            HTMLCollection.prototype[Symbol.iterator] = Array.prototype[Symbol.iterator];
            const textInputElements = document.querySelectorAll(`input[type="text"]`)
            for (const input of textInputElements) {
                if (input.scrollWidth > input.clientWidth) {
                    const inputId = input.id;
                    const label = document.querySelector(`label[for="${inputId}"]`) as HTMLElement;
                    let count = 0;
                    this.overflowedElements.push({
                        labelName: label.innerText,
                        fullText: (input as HTMLInputElement).value,
                        keyIndex: count++
                    });
                }
            }
        });
    }

    private addListenerToExternalButtons() {
        if (document.readyState !== 'loading') {
            this.doAddListenerToExternalButtons();
        } else {
            document.addEventListener('DOMContentLoaded', this.doAddListenerToExternalButtons);
        }
    }

    private doAddListenerToExternalButtons() {
        this.removeListenerFromExternalButtons();
        const submitButton = document.getElementById(this.SUBMIT_BUTTON_ID);
        if (submitButton) {
            submitButton.addEventListener("click", this.submitForm);
        }
        const pdfButton = document.getElementById(this.PDF_BUTTON_ID);
        if (pdfButton) {
            pdfButton.addEventListener("click", this.downloadPDF);
        }
    }

    private beforeDestroy() {
        document.removeEventListener('DOMContentLoaded', this.doAddListenerToExternalButtons);
        this.removeListenerFromExternalButtons();
    }

    private removeListenerFromExternalButtons() {
        const submitBUtton = document.getElementById(this.SUBMIT_BUTTON_ID);
        if (submitBUtton) {
            submitBUtton.removeEventListener("click", this.submitForm);
        }
        const pdfButton = document.getElementById(this.PDF_BUTTON_ID);
        if (pdfButton) {
            pdfButton.removeEventListener("click", this.downloadPDF);
        }
    }

    private async loadData() {
        await this.execute(async () => {
            const promises = [];
            const queryParams = getQueryParams();
            if (queryParams.formToken) {
                promises.push(getFormSubmission(queryParams.formToken)
                    .then((result) => this.formSubmission = result)
                    .catch((err) => {
                        this.$notifyError('Unable to load form from token.  It is either invalid or has expired.');
                    }));
            }
            const formName = this.getFormNameFromDiv();
            promises.push(getFormByName(formName).then((result) => this.form = result));
            await Promise.all(promises);
            this.sections = this.buildSectionAndFieldValues();
            this.uploads = this.buildUploadViewModels();
            await this.getOverflowData();
        }, 'Unable to load form.  Please contact BEK.');

        const buttonElement = document.getElementById(this.SUBMIT_BUTTON_ID);
        if (buttonElement) {
            if (this.formSubmission.submittedOn) {
                buttonElement.classList.add('disabled');
            } else {
                buttonElement.classList.remove('disabled');
            }
        }
    }

    private buildSectionAndFieldValues() {
        const formSubmissionFieldMap = this.buildFormFieldSubmissionMap();
        const sectionNeededRowMap = this.getRowsNeededPerSection(formSubmissionFieldMap);
        return this.form.formSections.map((formSection: FormSection) => {
            const sectionRowList = new Array(sectionNeededRowMap.get(formSection.id));
            sectionRowList.fill('');
            const sectionRowViewModels = sectionRowList.map((sectionRow, index) => {
                return this.createFormSectionRow(formSection, formSubmissionFieldMap, index);
            });
            return {...formSection, formSectionRows: sectionRowViewModels };
        });
    }

    private createFormSectionRow(formSection: FormSection, formSubmissionFieldMap: Map<number, Map<number, FormSubmissionField>>, index: number) {
        const rows = [] as SectionGridRow[];
        let currentRow: SectionGridRow | undefined;
        formSection.formSectionFields.forEach((formSectionField) => {
            const formSubmissionField = this.createFormSubmissionField(formSubmissionFieldMap, formSectionField, index);
            this.fields.push(formSubmissionField);

            if (formSectionField.fieldLength === 2 || !currentRow) {
                if (currentRow) {
                    rows.push(currentRow);
                    currentRow = undefined;
                }
                currentRow = {left: formSubmissionField};
                if (formSectionField.fieldLength === 2) {
                    rows.push(currentRow);
                    currentRow = undefined;
                }
            } else {
                currentRow.right = formSubmissionField;
                rows.push(currentRow);
                currentRow = undefined;
            }
        });
        if (currentRow) {
            rows.push(currentRow);
        }
        return {sectionGridRows: rows}
    }

    private buildFormFieldSubmissionMap() {
        const formSubmissionMap = new Map<number, Map<number, FormSubmissionField>>();
        if (this.formSubmission) {
            this.formSubmission.formSubmissionFields.forEach((field) => {
                let value = formSubmissionMap.get(field.formSectionFieldId);
                if (!value) {
                    value = new Map<number, FormSubmissionField>();
                    formSubmissionMap.set(field.formSectionFieldId, value);
                }
                value.set(field.formSectionRow, field);
            });
        }
        return formSubmissionMap;
    }

    private getRowsNeededPerSection(formSubmissionMap: Map<number, Map<number, FormSubmissionField>>) {
        return new Map(this.form.formSections.map((formSection: FormSection) => {
            let maxRow = formSection.defaultSectionRows;
            formSection.formSectionFields.forEach((formSectionField) => {
                const formSubmissionRowMap = formSubmissionMap.get(formSectionField.id);
                if (formSubmissionRowMap) {
                    maxRow = Math.max(maxRow, ...Array.from(formSubmissionRowMap.keys()).map((num) => num + 1 ));
                }
            });
            return [formSection.id, maxRow];
        }));
    }

    private createFormSubmissionField(formSubmissionFieldMap: Map<number, Map<number, FormSubmissionField>>, formSectionField: FormSectionField, row: number): FormSubmissionField {
        let formSubmissionField = this.findSubmissionFieldFromMap(formSubmissionFieldMap, formSectionField, row) ||
            this.createValuelessFormSubmissionField(formSectionField, row);

        if (formSectionField.keyField === KeyField.EMAIL) {
            this.primaryEmailField = formSubmissionField;
        }
        return formSubmissionField;
    }

    private findSubmissionFieldFromMap(formSubmissionFieldMap: Map<number, Map<number, FormSubmissionField>>, formSectionField: FormSectionField, row: number) {
        let formSubmissionFieldRowMap = formSubmissionFieldMap.get(formSectionField.id);
        if (formSubmissionFieldRowMap) {
            const formSubmissionFieldFromMap = formSubmissionFieldRowMap.get(row);
            if (formSubmissionFieldFromMap) {
                return {
                    ...formSubmissionFieldFromMap,
                    formSectionField,
                }
            }
        }
        return undefined;
    }

    private createValuelessFormSubmissionField(formSectionField: FormSectionField, row: number) {
        return {
            id: 0,
            formSubmissionId: '',
            formSectionField,
            formSectionRow: row,
            formSectionFieldId: formSectionField.id,
            fieldValue: '',
        };
    }

    private buildUploadViewModels() {
        const formSubmissionUploadMap = this.formSubmission ? this.buildFormSubmissionUploadMap() : new Map();
        return this.form.formUploads.map((formUpload: FormUpload) => {
            let formSubmissionUpload = formSubmissionUploadMap.get(formUpload.id);
            if (formSubmissionUpload) {
                return {
                    ...formSubmissionUpload,
                    formUpload,
                    complete: formSubmissionUpload.s3Keys.length > 0
                }
            } else {
                return {
                    id: 0,
                    formSubmissionId: '',
                    formUploadId: formUpload.id,
                    formUpload,
                    s3Keys: [],
                    complete: false
                };
            }
        });
    }

    private buildFormSubmissionUploadMap() {
        const formSubmissionUploadMap = new Map();
        this.formSubmission.formSubmissionUploads.forEach((upload) => {
            let object = formSubmissionUploadMap.get(upload.formUploadId);
            if (!object) {
                object = {
                    formSubmissionId: upload.formSubmissionId,
                    formUploadId: upload.formUploadId,
                    s3Keys: [],
                    complete: false
                };
                formSubmissionUploadMap.set(upload.formUploadId, object);
            }
            object.s3Keys.push(upload.s3Key);
        });
        return formSubmissionUploadMap;
    }


    private getFormNameFromDiv() {
        let formName;
        let typeElement = document.getElementById('custom-form-name');
        if (typeElement) {
            formName = typeElement.getAttribute('data-custom-form-name');
        }
        if (!formName) {
            console.error('Unable to load custom form, no name set');
            throw new Error('Unable to load form.  Please contact BEK.')
        }
        return formName;
    }

    private async submitForm() {
        if (this.formSubmission.submittedOn) {
            return;
        }
        if (this.captchaResponse) {
            this.captchaError = '';    
            const captchaResult: CaptchaResult = { captchaResponse: this.captchaResponse };
                const validation = await HTTP.post(`/public/captcha/validate/entry`, {
                    ...captchaResult
                });
                if (validation && validation.success) {       
            const formSubmission = this.buildFormSubmissionForSave();
            await this.save(async () => {
                formSubmission.submittedOn = new Date();
                if (this.formSubmission.id) {
                    await updateFormSubmission(formSubmission);
                    await this.loadData();
                } else {
                    await createFormSubmission(formSubmission);
                }
                this.formSubmission.submittedOn = formSubmission.submittedOn;
                this.$notifySuccess('Form submitted. You should receive email confirmation shortly.');
                window.onbeforeunload = null;
                const buttonElement = document.getElementById(this.SUBMIT_BUTTON_ID);
                if (buttonElement) {
                    buttonElement.classList.add('disabled');
                }
                this.formSubmission.submittedOn = formSubmission.submittedOn;
            }, 'Failed to submit form.  Please contact BEK or try again later.');
            const errorElements = document.getElementsByClassName("input-error show");
            if (errorElements[0]) {
                errorElements[0].scrollIntoView({behavior: "smooth", block: "center"});
            }
            }
            else{
                
                this.captchaError = 'Captcha failed. Please refresh this page and try again.';
            }
        }
    }

    private buildFormSubmissionForSave(): FormSubmission {
        const fields = this.sections.flatMap((section) => {
            return section.formSectionRows.flatMap((formSelectionRow) => {
                return formSelectionRow.sectionGridRows.flatMap((sectionGridRow) => {
                    if (!sectionGridRow.right) {
                        return [sectionGridRow.left];
                    } else {
                        return [sectionGridRow.left, sectionGridRow.right]
                    }
                });
            });
        });
        const formSubmissionUploads = [] as FormSubmissionUpload[];
        this.uploads.forEach((upload) => {
            upload.s3Keys.forEach((s3Key: string) => {
                formSubmissionUploads.push({
                    id: 0,
                    formSubmissionId: '',
                    formUploadId: upload.formUploadId,
                    s3Key
                });
            });
        });
        const url = window.location.origin + window.location.pathname;
        const formSubmission = {
            id: this.formSubmission.id,
            formId: this.form.id,
            url: url,
            saveForLater: false,
            formSubmissionFields: fields,
            formSubmissionUploads,
            userSavedFormOn: this.formSubmission.userSavedFormOn,
            printedToPdf: this.formSubmission.printedToPdf
        };
        return formSubmission;
    }

    private async downloadPDF() {
        if (this.captchaResponse) {
            this.captchaError = '';
            
            const captchaResult: CaptchaResult = { captchaResponse: this.captchaResponse };
                const validation = await HTTP.post(`/public/captcha/validate/entry`, {
                    ...captchaResult
                });
                if (validation && validation.success) {   
            const formSubmission = this.buildFormSubmissionForSave();
            formSubmission.submittedOn = null;
            formSubmission.printedToPdf = true;
            await this.save(async () => {
                if (this.formSubmission.id) {
                    await updateFormSubmission(formSubmission);
                } else {
                    this.formSubmission.id = await createFormSubmission(formSubmission);
                }
                const pdf = await downloadFormSubmission(this.formSubmission.id);
                downloadFile(pdf, `${this.form.name.replace(' ', '_')}.pdf`);
                window.onbeforeunload = null;
            }, 'Failed to download PDF.  Please contact BEK or try again later.');   
            
        }
        else{
            
            this.captchaError = 'Captcha failed. Please refresh this page and try again.';
        }         
        }
    }

    private async finishLater() {
        const formSubmission = this.buildFormSubmissionForSave();
        formSubmission.submittedOn = null;
        formSubmission.saveForLater = true;
        formSubmission.userSavedFormOn = new Date();
        await this.save(async () => {
            if (this.formSubmission.id) {
                await updateFormSubmission(formSubmission);
            } else {
                this.formSubmission.id = await createFormSubmission(formSubmission);
            }
            this.formSubmission.userSavedFormOn = formSubmission.userSavedFormOn;
            this.$notifySuccess('Form saved. You should receive an email with the appropriate link shortly.');
            window.onbeforeunload = null;
        }, 'Failed to download PDF.  Please contact BEK or try again later.');
    }

    private async autoSave() {
        const formSubmission = this.buildFormSubmissionForSave();
        formSubmission.submittedOn = null;
        try {
            this.autoSaving = true;
            if (this.formSubmission.id) {
                await updateFormSubmission(formSubmission);
            } else {
                this.formSubmission.id = await createFormSubmission(formSubmission);
            }
        } catch (e) {
            //silently fail since it was not user originated
        } finally {
            this.autoSaving = false;
        }

    }

    private showSaveLaterModal() {
        this.tempEmailField = this.primaryEmailField.fieldValue;
        this.showModal = true;
    }

    private cancelSaveLaterModal() {
        this.tempEmailField = '';
        this.showModal = !this.showModal;
    }

    private async finishLaterAndEmail() {
        if (!this.tempEmailField) {
           return;
        }
        this.primaryEmailField.fieldValue = this.tempEmailField;
        this.showModal = false;
        await this.finishLater();
    }

    private canAddMoreRows(section: FormSectionViewModel) {
        return section.defaultSectionRows > 1 && section.formSectionRows.length + section.rowsPerPage < section.maximumSectionRows;
    }

    private addMoreRows(section: FormSectionViewModel) {
        const currentIndex = section.formSectionRows.length;
        Array.from(Array(section.rowsPerPage)).forEach((x, i) => {
            section.formSectionRows.push(this.createFormSectionRow(section, new Map(), i + currentIndex));
        });
    }
}
